'use client';

import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSession } from 'next-auth/react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Route } from 'next';
import ProfileDropdown from '@/components/ProfileDropdown';
import {
  Shield,
  Globe,
  Swords,
  ChevronDown,
  Menu,
  X,
  Building2,
  Users,
  Briefcase,
  HelpCircle,
  BookOpen,
  GraduationCap,
  ArrowRight,
} from 'lucide-react';

// ---------- Types ----------
type LeafItem = { href: Route; label: string; icon?: React.ReactNode; desc?: string; external?: boolean };
type ParentItem = { label: string; children: readonly LeafItem[] };
type NavItem = LeafItem | ParentItem;
const isParent = (x: NavItem): x is ParentItem => 'children' in x;

// ---- Navigation config
const NAV: readonly NavItem[] = [
  { href: '/autofix' as Route, label: 'AutoFix' },
  { href: '/cyberswarm' as Route, label: 'Cyber Swarm' },
  { href: '/cybercrawler' as Route, label: 'Cyber Crawler' },
  {
    label: 'Company',
    children: [
      {
        href: '/about' as Route,
        label: 'About',
        icon: <Building2 className="w-4 h-4" />,
        desc: 'Our mission and story',
      },
      {
        href: '/careers' as Route,
        label: 'Careers',
        icon: <Briefcase className="w-4 h-4" />,
        desc: 'Join our team',
      },
      {
        href: '/faq' as Route,
        label: 'FAQ',
        icon: <HelpCircle className="w-4 h-4" />,
        desc: 'Common questions',
      },
      {
        href: '/blog' as Route,
        label: 'Blog',
        icon: <BookOpen className="w-4 h-4" />,
        desc: 'Security research & insights',
      },
      {
        href: 'https://quiz.cyberarmy.ai/' as Route,
        label: 'Cyber Quiz',
        icon: <GraduationCap className="w-4 h-4" />,
        desc: 'Test your security knowledge',
        external: true,
      },
    ] as const,
  } as const,
] as const;

// ---------- Hook: scroll state ----------
function useScrollState() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 20);
      if (y > 100) {
        setHidden(y > lastScrollY.current && y - lastScrollY.current > 5);
      } else {
        setHidden(false);
      }
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return { scrolled, hidden };
}

// ---------- Reusable link ----------
function NavLink({ href, label }: LeafItem) {
  const pathname = usePathname();
  const active = pathname === href || pathname?.startsWith((href as string) + '/');
  return (
    <Link
      href={href}
      className={`
        relative text-[13px] font-medium tracking-wide transition-colors duration-200
        ${active ? 'text-white' : 'text-zinc-400 hover:text-zinc-100'}
      `}
    >
      {label}
      {active && (
        <motion.span
          layoutId="nav-indicator"
          className="absolute -bottom-1.5 left-0 right-0 h-[2px] bg-accent rounded-full"
          transition={{ type: 'spring', stiffness: 380, damping: 30 }}
        />
      )}
    </Link>
  );
}

// ---- Desktop dropdown - opens on hover
function NavDesktopGroup({ label, children }: ParentItem) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };

  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) handleLeave();
      }}
    >
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        className={`
          cursor-default text-[13px] font-medium tracking-wide
          inline-flex items-center gap-1 focus:outline-none transition-colors duration-200
          ${open ? 'text-zinc-100' : 'text-zinc-400 hover:text-zinc-100'}
        `}
      >
        {label}
        <ChevronDown
          className={`w-3.5 h-3.5 opacity-50 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
            className="absolute left-1/2 -translate-x-1/2 mt-3 w-64 rounded-2xl
                       border border-zinc-800/80 bg-zinc-950/95 backdrop-blur-xl
                       shadow-2xl shadow-black/40 p-2 overflow-hidden"
          >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />

            {children.map((c) => (
              <Link
                key={c.href}
                href={c.href}
                target={c.external ? '_blank' : undefined}
                rel={c.external ? 'noopener noreferrer' : undefined}
                className="group flex items-start gap-3 rounded-xl px-3 py-2.5
                           text-zinc-300 hover:bg-zinc-800/50 hover:text-white
                           transition-all duration-200"
                role="menuitem"
              >
                {c.icon && (
                  <span className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg
                                   bg-accent/10 border border-accent/20
                                   flex items-center justify-center text-accent
                                   group-hover:bg-accent/15 group-hover:border-accent/30
                                   transition-all duration-200">
                    {c.icon}
                  </span>
                )}
                <div className="min-w-0">
                  <div className="text-sm font-medium">{c.label}</div>
                  {c.desc && (
                    <div className="text-xs text-zinc-500 group-hover:text-zinc-400 transition-colors mt-0.5">
                      {c.desc}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------- iOS-safe scroll lock ----------
function useBodyScrollLock(open: boolean) {
  useEffect(() => {
    if (!open) return;
    const y = window.scrollY;
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${y}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    return () => {
      const top = body.style.top;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      window.scrollTo(0, top ? -parseInt(top) : 0);
    };
  }, [open]);
}

// ---------- Mobile drawer ----------
function MobileDrawer({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] md:hidden">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="absolute top-0 right-0 h-full w-[86%] max-w-xs
                       border-l border-white/[0.06] shadow-2xl flex flex-col"
            role="dialog"
            aria-modal="true"
            style={{
              backgroundColor: 'var(--surface-base)',
              paddingTop: 'max(1rem, env(safe-area-inset-top))',
              paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
            }}
          >
            {children}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}

// ---------- Mobile collapsible group ----------
function MobileGroup({ label, children, onNavigate }: ParentItem & { onNavigate: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full rounded-xl px-4 py-3 text-[15px] text-zinc-200 
                   hover:bg-zinc-800/40 flex items-center justify-between transition-colors"
        aria-expanded={open}
      >
        <span>{label}</span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="ml-3 pl-3 border-l border-zinc-800/60 py-1">
              {children.map((c) => (
                <Link
                  key={c.href}
                  href={c.href}
                  target={c.external ? '_blank' : undefined}
                  rel={c.external ? 'noopener noreferrer' : undefined}
                  onClick={() => { setOpen(false); onNavigate(); }}
                  className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-[14px] text-zinc-400 hover:text-white hover:bg-zinc-800/30 transition-colors"
                >
                  {c.icon && (
                    <span className="text-accent/70">{c.icon}</span>
                  )}
                  {c.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------- Header ----------
export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const { data: session, status } = useSession();
  const { scrolled, hidden } = useScrollState();
  useBodyScrollLock(open);

  return (
    <motion.header
      initial={false}
      animate={{ y: 0 }}
      transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
      className={`
        fixed top-0 left-0 right-0 z-50 transition-all duration-500
        ${scrolled
          ? 'bg-zinc-950/80 backdrop-blur-2xl border-b border-zinc-800/40 shadow-lg shadow-black/20'
          : 'bg-transparent border-b border-transparent'
        }
      `}
    >
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2
                   bg-accent text-black rounded-lg px-3 py-1.5 text-sm font-medium"
      >
        Skip to content
      </a>

      <div className={`
        max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between
        transition-all duration-500
        ${scrolled ? 'h-16' : 'h-20'}
      `}>
        {/* Logo - Left */}
        <Link href={'/' as Route} className="flex items-center gap-3 flex-shrink-0">
          <Image
            src="/press/cyberarmy-logo.png"
            alt="Cyber Army Logo"
            width={261}
            height={144}
            className={`
              w-auto rounded-md transition-all duration-500
              ${scrolled ? 'h-10' : 'h-12 md:h-14'}
            `}
            priority
          />
          <span className={`
            font-semibold tracking-tight transition-all duration-500
            ${scrolled ? 'text-sm' : 'text-base md:text-lg'}
          `}>
            Cyber Army&trade;
          </span>
        </Link>

        {/* Nav - Center */}
        <nav className="hidden md:flex items-center gap-7 absolute left-1/2 -translate-x-1/2">
          {NAV.map((n) =>
            isParent(n) ? (
              <NavDesktopGroup key={n.label} {...n} />
            ) : (
              <NavLink key={(n as LeafItem).href} {...(n as LeafItem)} />
            )
          )}
        </nav>

        {/* CTA + Auth - Right */}
        <div className="hidden md:flex items-center gap-3 flex-shrink-0">
          {status === 'loading' ? (
            <div className="w-20 h-9 bg-zinc-800/50 rounded-xl animate-pulse" />
          ) : session ? (
            <ProfileDropdown user={session.user} />
          ) : null}
          <Link
            href={'/contact' as Route}
            className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-xl
                       bg-accent text-black text-sm font-semibold
                       hover:bg-accent-hover transition-all duration-300
                       shadow-md shadow-accent/10 hover:shadow-accent/25
                       hover:scale-[1.02] active:scale-[0.98]"
          >
            Contact
            <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>

        {/* Mobile menu button */}
        <div className="md:hidden flex items-center gap-2">
          <button
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            aria-expanded={open}
            aria-controls="mobile-menu"
            className="inline-flex items-center justify-center w-10 h-10 rounded-xl
                       border border-zinc-800/60 bg-zinc-900/50 backdrop-blur-sm
                       text-zinc-100 hover:border-zinc-600 hover:bg-zinc-800/50 transition-all duration-200"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      <MobileDrawer open={open} onClose={() => setOpen(false)}>
        <div className="px-5 pb-4 pt-1 flex items-center justify-between">
          <Link href={'/' as Route} className="flex items-center gap-3" onClick={() => setOpen(false)}>
            <Image
              src="/press/cyberarmy-logo.png"
              alt="Cyber Army Logo"
              width={261}
              height={144}
              className="h-10 w-auto rounded-md"
              priority
            />
            <span className="text-base font-semibold">Cyber Army&trade;</span>
          </Link>
          <button
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            className="inline-flex items-center justify-center w-9 h-9 rounded-xl
                       border border-zinc-800/60 hover:border-zinc-600
                       text-zinc-300 hover:text-white transition-all duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav id="mobile-menu" className="px-3 flex-1 overflow-y-auto">
          <div className="grid gap-0.5 py-2">
            {NAV.map((n) =>
              isParent(n) ? (
                <MobileGroup
                  key={n.label}
                  {...n}
                  onNavigate={() => setOpen(false)}
                />
              ) : (
                <Link
                  key={(n as LeafItem).href}
                  href={(n as LeafItem).href}
                  onClick={() => setOpen(false)}
                  className="rounded-xl px-4 py-3 text-[15px] text-zinc-200 hover:bg-zinc-800/40 transition-colors"
                >
                  {(n as LeafItem).label}
                </Link>
              )
            )}
          </div>

          <div className="px-1 pt-4 pb-2 border-t border-zinc-800/40 mt-2">
            <Link
              href={'/contact' as Route}
              onClick={() => setOpen(false)}
              className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl
                         bg-accent text-black text-sm font-semibold
                         hover:bg-accent-hover transition-all duration-300"
            >
              Request Early Access
              <ArrowRight className="w-4 h-4" />
            </Link>

            {status === 'loading' ? (
              <div className="mt-3 w-full h-10 bg-zinc-800/50 rounded-xl animate-pulse" />
            ) : session ? (
              <div className="mt-3 pt-3 border-t border-zinc-800/40">
                <ProfileDropdown user={session.user} />
              </div>
            ) : null}
          </div>

          {/* Trust block */}
          <div className="mt-4 mx-1 rounded-xl border border-white/[0.05] bg-surface-1 p-4 space-y-3">
            <a href="tel:+14259858004" className="flex items-center gap-3 group">
              <div className="w-7 h-7 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-3.5 h-3.5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <span className="text-xs text-zinc-400 group-hover:text-accent transition-colors">425.985.8004</span>
            </a>
            <a href="mailto:contact@cyberarmy.ai" className="flex items-center gap-3 group">
              <div className="w-7 h-7 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-3.5 h-3.5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-xs text-zinc-400 group-hover:text-accent transition-colors">contact@cyberarmy.ai</span>
            </a>
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3.5 h-3.5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <span className="text-xs text-zinc-400 leading-relaxed">440 N Wolfe Rd<br/>Sunnyvale, CA 94085</span>
            </div>
            <a href="https://www.linkedin.com/company/cyber-army/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 group">
              <div className="w-7 h-7 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-3.5 h-3.5 text-accent" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </div>
              <span className="text-xs text-zinc-400 group-hover:text-accent transition-colors">linkedin.com/company/cyber-army</span>
            </a>
            <div className="pt-2 border-t border-white/[0.04] text-[10px] text-zinc-700">
              Sunnyvale, CA &middot; OWASP/NIST aligned
            </div>
          </div>
        </nav>
      </MobileDrawer>
    </motion.header>
  );
}
